import { useSelector } from '@xstate/react';
import { CanceledError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { UserContext, useAlertView, useConfirmationView } from '~/app';
import { Button } from '~/components/buttons';
import { getActivationPreview } from '~/features/employer/billing/api';
import type {
  ActivationPreview,
  FeeAuthorization,
  FeeSchedule,
} from '~/features/employer/billing/types';
import { useManagePaymentMethod } from '~/features/employer/billing/useManagePaymentMethod';
import {
  FEE_AUTHORIZATION_REQUIRED_MESSAGE,
  FEE_SCHEDULE_CHANGED_MESSAGE,
} from '~/features/market/listing-search/errors';
import {
  JOB_TERM_GROUPS,
  formatNegotiationHistoryTerm,
  headlineValue,
  jobChainToVersions,
  latestOf,
  negotiationHistoryTermLabel,
} from '~/features/market/negotiations/negotiationHistory';
import { ResponseNegotiationModal } from '~/features/market/negotiations/ResponseNegotiationModal';
import { ActivationFeeDialog } from '~/features/market/negotiations/sub-components/ActivationFeeDialog';
import { useNegotiationChain } from '~/features/market/negotiations/useNegotiationChain';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { useDeepLinkedResponseSelection } from '~/features/shared/deepLinkParams';
import type { MarketFact } from '~/features/shared/market-content';
import {
  MarketActionRow,
  MarketAlert,
  MarketContentPanel,
  MarketEmptyPanel,
  MarketFactGrid,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketPartySummary,
  MarketResponseListItem,
  MarketSegmentedControl,
  MarketShowDeclinedToggle,
  MarketStatusPill,
  MarketTurnPill,
  MarketWorkspaceGrid,
  formatResponseStatusLabel,
  formatStatusLabel,
} from '~/features/shared/market-content';
import { MessageComposer } from '~/features/shared/market-content/MessageComposer';
import { NegotiationHistory } from '~/features/shared/market-content/NegotiationHistory';
import {
  ResponseUnmaskPanel,
  selectUnmaskViewerScope,
} from '~/features/shared/market-content/ResponseUnmaskPanel';
import { OpportunityRecoveryNotice } from '~/features/shared/OpportunityRecoveryNotice';
import {
  isReceivedResponseVisible,
  reconcileReceivedResponseRows,
} from '~/features/shared/receivedResponseRows';
import { declineResponseConfirmation } from '~/features/shared/responseCloseConfirmations';
import {
  ReceivedResponseActionsBoundary,
  useReceivedResponseActions,
} from '~/features/shared/useReceivedResponseActions';
import { useResponseDeclinedToast } from '~/features/shared/useResponseDeclinedToast';
import { extractBackendErrorDetail } from '~/features/utils';
import { buildProviderViewUrl } from '~/routing';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { ContractReadyNotice } from '../../contracts/ContractReadyNotice';
import { MessagesSection } from '../../job-opportunities/sub-components/MessagesSection';
import { formatDate, formatHourlyRate, formatWorkLocation } from '../../job-opportunities/utils';
import { API_CALLS } from './api';
import type {
  ProviderJobMessage,
  ProviderJobResponse,
  ProviderJobResponseAction,
  ProviderJobResponseFilter,
} from './types';

// Job Responses is the employer's page, so the employer's rounds are "You".
const VIEWER_SENDER = 'EMPLOYER';

type ActionName = 'accept' | 'accept_listing' | 'counter' | 'withdraw' | 'reject';

const INVALID_LISTING_FILTER_MESSAGE = 'Invalid listing filter; showing all responses.';
const MISSING_LISTING_FILTER_MESSAGE = 'Filter listing not found; showing all responses.';

// [MCV-1549] Takes the search string rather than reading window.location, so
// the caller can derive it from the router location. A second notification for
// this same route changes only the query string — read once at mount, a new
// listingId never reloads and a new responseId never selects.
const parseListingId = (search: string): { listingId?: number; warning?: string } => {
  const value = new URLSearchParams(search).get('listingId');
  if (!value) return {};
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return { warning: INVALID_LISTING_FILTER_MESSAGE };
  }
  return { listingId: parsed };
};

const getErrorMessage = (err: unknown, fallback: string) => {
  const response =
    err != null && typeof err === 'object' && 'response' in err
      ? (err as { response?: { status?: number; data?: { detail?: unknown } } }).response
      : undefined;
  const backendDetail = extractBackendErrorDetail(response?.data?.detail);

  if (response?.status === 409) {
    return backendDetail ?? "This listing isn't ready for negotiation.";
  }

  return backendDetail ?? (err instanceof Error ? err.message : fallback);
};

const hasAction = (actions: ProviderJobResponseAction[], action: ProviderJobResponseAction) =>
  actions.includes(action);

const hasListingMatch = (rows: ProviderJobResponse[], listingId: number) =>
  rows.some((row) => row.listing.id === listingId);

// [MCV-1549] The full identity of a load's inputs. Compared before a request is
// allowed to claim a generation, and again before its result is applied.
type ListingQuery = {
  listingId?: number;
  filter: ProviderJobResponseFilter;
  includeDeclined: boolean;
  warning: string | null;
};

const isSameQuery = (a: ListingQuery, b: ListingQuery) =>
  a.listingId === b.listingId &&
  a.filter === b.filter &&
  a.includeDeclined === b.includeDeclined &&
  a.warning === b.warning;

const getResponseStatusTone = (
  status: string
): Parameters<typeof MarketStatusPill>[0]['status'] => {
  if (status === 'ACCEPTED') return 'accepted';
  if (status === 'REJECTED') return 'danger';
  if (status === 'WITHDRAWN') return 'inactive';
  if (status === 'NEGOTIATING' || status === 'PENDING_ACCEPTANCE') return 'pending';
  return 'neutral';
};

export const ProviderJobResponses = () => {
  const consentViewerScope = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return (
    <ReceivedResponseActionsBoundary>
      <ScopedProviderJobResponses
        key={consentViewerScope}
        consentViewerScope={consentViewerScope}
      />
    </ReceivedResponseActionsBoundary>
  );
};

const ScopedProviderJobResponses = ({ consentViewerScope }: { consentViewerScope: string }) => {
  const { search } = useLocation();
  const listingFilter = useMemo(() => parseListingId(search), [search]);
  const listingId = listingFilter.listingId;
  const userRef = UserContext.useActorRef();
  const currentUserInfoId = useSelector(userRef, (s) => s.context.userInfoId);

  const [filter, setFilter] = useState<ProviderJobResponseFilter>('all');
  // [MCV-1850] Declined and withdrawn responses are hidden unless asked for.
  const [includeDeclined, setIncludeDeclined] = useState(false);
  const [responses, setResponses] = useState<ProviderJobResponse[]>([]);
  const [selectedResponseId, setSelectedResponseId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [messagesReloadNonce, setMessagesReloadNonce] = useState(0);
  const [negotiationResponseId, setNegotiationResponseId] = useState<number | null>(null);
  const [negotiationStartDraftIfEmpty, setNegotiationStartDraftIfEmpty] = useState(false);
  const [activationPreview, setActivationPreview] = useState<ActivationPreview | null>(null);
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  // [MCV-1933] Which write the open dialog confirms: accepting a real offer (`accept`) or an
  // application on the listing's terms (`accept_listing`). A refused fee schedule reopens it with
  // the reason in `activationNotice`.
  const [activationKind, setActivationKind] = useState<'accept' | 'accept_listing'>(
    'accept_listing'
  );
  const [activationNotice, setActivationNotice] = useState<string | null>(null);
  const [previewingActivation, setPreviewingActivation] = useState(false);
  // The response the open preview/dialog belongs to. The sidebar stays
  // selectable while the preview is in flight, so both the resolved preview
  // and the Confirm click are checked against the current selection.
  const [activationResponseId, setActivationResponseId] = useState<number | null>(null);
  const alertView = useAlertView();
  const confirmationView = useConfirmationView();
  const handleManagePaymentMethod = useManagePaymentMethod();
  const [error, setError] = useState<string | null>(null);
  const [listingFilterWarning, setListingFilterWarning] = useState<string | null>(
    listingFilter.warning ?? null
  );
  // [MCV-1549] listingId is reactive, so a notification can start a second load
  // while the first is still in flight. Without this, listing A's response
  // resolving after listing B's would overwrite B's rows and selection while
  // the URL still names B — exposing actions for the wrong applicant.
  const loadGenerationRef = useRef(0);
  const listReadRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      listReadRef.current?.abort();
    };
  }, []);
  const [reloadNonce, setReloadNonce] = useState(0);
  // Start order alone is not enough. An action begun on listing A can await,
  // the user can follow a notification to B and let B finish, and only then
  // does A's captured `loadResponses` closure run — taking the *highest*
  // generation and passing every ordering check while querying A. What makes a
  // result valid is that its inputs still match the current ones.
  //
  // The warning is part of the identity, not decoration: navigating between no
  // `listingId` and an invalid one such as `?listingId=abc` yields the same
  // normalised `{ listingId: undefined, filter: 'all' }`, so without it a
  // refresh captured under one URL is accepted under the other and overwrites
  // the current invalid-filter warning.
  const listingFilterWarningForQuery = listingFilter.warning ?? null;
  const currentQueryRef = useRef<ListingQuery>({
    listingId,
    filter,
    includeDeclined,
    warning: listingFilterWarningForQuery,
  });
  currentQueryRef.current = {
    listingId,
    filter,
    includeDeclined,
    warning: listingFilterWarningForQuery,
  };

  const selected = useMemo(
    () => responses.find((row) => row.responseId === selectedResponseId) ?? null,
    [responses, selectedResponseId]
  );
  const selectedResponseIdRef = useRef(selectedResponseId);
  selectedResponseIdRef.current = selectedResponseId;
  // The in-flight activation preview. A stalled read must not pin the
  // controls: the request is bounded by MARKET_READ_TIMEOUT_MS and aborted
  // when the selection changes or the workspace unmounts.
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewResponseIdRef = useRef<number | null>(null);
  useEffect(() => {
    // Abort only a preview for another response. This effect can run after an
    // Accept click that landed right after auto-select, and aborting then
    // would silently cancel that click's own preview.
    if (previewResponseIdRef.current !== selectedResponseId) previewAbortRef.current?.abort();
  }, [selectedResponseId]);
  useEffect(() => () => previewAbortRef.current?.abort(), []);
  const actions = useReceivedResponseActions(
    selected
      ? {
          workspace: 'job',
          listingId: selected.listing.id,
          responseId: selected.responseId,
          expectedNegotiationId: selected.latestNegotiation?.id,
        }
      : null
  );
  const { messages, messagesError, messagesLoading, refreshed } = actions;
  useResponseDeclinedToast(actions.receipt);
  const recoveryRef = useRef(refreshed);
  if (refreshed) recoveryRef.current = refreshed;
  const actionLoading =
    actions.busy && actions.pending?.kind !== 'reply' ? (actions.pending?.kind ?? null) : null;
  useEffect(() => {
    if (refreshed?.workspace !== 'job') return;
    const { filter: queryFilter, includeDeclined: queryIncludeDeclined } = currentQueryRef.current;
    setResponses((current) =>
      reconcileReceivedResponseRows(
        current,
        refreshed.action,
        refreshed.response,
        queryFilter,
        queryIncludeDeclined
      )
    );
    // A row that left the list (withdrawn, or declined with the checkbox off) clears the
    // detail pane rather than leaving it on a response the list no longer shows.
    if (!isReceivedResponseVisible(refreshed.response, queryFilter, queryIncludeDeclined))
      setSelectedResponseId((current) =>
        current === refreshed.action.responseId ? null : current
      );
  }, [refreshed]);

  // [MCV-1549] The listing filter alone only narrows the sidebar, and this view
  // then selected rows[0], so every notification about a listing with more than
  // one response opened the same wrong conversation.
  const requestDeepLinkReload = useCallback(() => {
    // The target may be newer than these rows, or hidden by the status filter,
    // and neither listingId nor filter necessarily changed — so nothing else
    // would fetch it. Reset to 'all' because a filtered query cannot return a
    // row the filter excludes, and bump the nonce so an already-'all' view
    // still refetches.
    setFilter('all');
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const { consumePendingResponseId, peekPendingResponseId } = useDeepLinkedResponseSelection({
    rows: responses,
    onSelect: setSelectedResponseId,
    onRequestReload: requestDeepLinkReload,
  });

  const applyRows = useCallback(
    (rows: ProviderJobResponse[]) => {
      setResponses((current) =>
        rows.map((row) => {
          const existing = current.find((item) => item.responseId === row.responseId);
          if (existing?.viewedAt && !row.viewedAt) {
            return { ...row, viewedAt: existing.viewedAt };
          }
          return row;
        })
      );
      const requested = consumePendingResponseId(rows);
      setSelectedResponseId((current) => {
        // A pending deep link outranks a surviving selection. This reload was
        // very likely requested *because* of that link, and the user's previous
        // row usually still exists — so keeping it would silently discard the
        // notification they just clicked.
        if (requested != null) return requested;
        if (current != null && rows.some((row) => row.responseId === current)) return current;
        return rows[0]?.responseId ?? null;
      });
    },
    [consumePendingResponseId]
  );

  const loadResponses = useCallback(
    async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      const query = {
        listingId,
        filter,
        includeDeclined,
        warning: listingFilterWarningForQuery,
      };
      const recoveryAtStart = recoveryRef.current;
      // Checked *before* claiming a generation. A route-stale invocation that
      // took one would invalidate the live request for the current route: the
      // stale call then rejects itself on this same check, the live call rejects
      // itself on the generation the stale one stole, and neither applies rows
      // nor clears the spinner — the URL names B while the page sits on A.
      if (!mountedRef.current || !isSameQuery(currentQueryRef.current, query)) return;

      listReadRef.current?.abort();
      const controller = new AbortController();
      listReadRef.current = controller;
      const generation = (loadGenerationRef.current += 1);
      const isCurrent = () =>
        !controller.signal.aborted &&
        loadGenerationRef.current === generation &&
        isSameQuery(currentQueryRef.current, query);
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const { rows, warning, widened } = await withRequestDeadline(
          async (signal) => {
            let rows = await API_CALLS.getListingResponses({
              filter,
              listingId,
              includeDeclined,
              signal,
            });
            signal.throwIfAborted();
            if (listingId != null && rows.length === 0) {
              // Whether the listing exists at all is judged on every status, so a listing
              // whose only responses are declined is not reported as missing.
              const listingRows =
                filter === 'all' && includeDeclined
                  ? rows
                  : await API_CALLS.getListingResponses({
                      filter: 'all',
                      listingId,
                      includeDeclined: true,
                      signal,
                    });
              signal.throwIfAborted();
              if (!hasListingMatch(listingRows, listingId)) {
                rows = await API_CALLS.getListingResponses({
                  filter,
                  listingId: undefined,
                  includeDeclined,
                  signal,
                });
                return { rows, warning: MISSING_LISTING_FILTER_MESSAGE, widened: false };
              }
            }
            // [MCV-1850] The linked response may be declined or withdrawn, which the default
            // list hides. Look again with those included, and keep the checkbox in step.
            const pendingResponseId = peekPendingResponseId();
            if (
              pendingResponseId != null &&
              !includeDeclined &&
              !rows.some((row) => row.responseId === pendingResponseId)
            ) {
              const wider = await API_CALLS.getListingResponses({
                filter,
                listingId,
                includeDeclined: true,
                signal,
              });
              signal.throwIfAborted();
              if (wider.some((row) => row.responseId === pendingResponseId))
                return { rows: wider, warning: listingFilterWarningForQuery, widened: true };
            }
            return { rows, warning: listingFilterWarningForQuery, widened: false };
          },
          MARKET_READ_TIMEOUT_MS,
          controller.signal
        );
        if (!isCurrent()) return;
        setListingFilterWarning(warning);
        const latest = recoveryRef.current;
        applyRows(
          latest?.workspace === 'job' && latest !== recoveryAtStart
            ? reconcileReceivedResponseRows(
                rows,
                latest.action,
                latest.response,
                filter,
                includeDeclined
              )
            : rows
        );
        if (widened) setIncludeDeclined(true);
      } catch (err) {
        if (!isCurrent()) return;
        setListingFilterWarning(listingFilterWarningForQuery);
        setError(getErrorMessage(err, 'Failed to load job responses.'));
      } finally {
        // The current load settles the spinner regardless of who raised it.
        // Gating on `showLoading` too left the page stuck: an effect load
        // raises it, a later action refresh (showLoading: false) supersedes
        // that generation, the older load is then barred from clearing and the
        // newer one skips the clear — so nothing lowered it until the next
        // effect load. An older or superseded load still must not clear it.
        if (isCurrent()) setLoading(false);
      }
    },
    [
      applyRows,
      filter,
      includeDeclined,
      listingFilterWarningForQuery,
      listingId,
      peekPendingResponseId,
    ]
  );

  useEffect(() => {
    void loadResponses({ showLoading: true });
    return () => listReadRef.current?.abort();
    // reloadNonce is the deep-link reload trigger: the target may be missing
    // without listingId or filter having changed, so nothing else re-runs this.
  }, [loadResponses, reloadNonce]);

  const selectedResponseIdForEffects = selected?.responseId;
  const selectedViewedAt = selected?.viewedAt;

  useEffect(() => {
    if (selectedResponseIdForEffects == null || selectedViewedAt != null) return;
    const controller = new AbortController();
    withRequestDeadline(
      (signal) => API_CALLS.markViewed(selectedResponseIdForEffects, signal),
      MARKET_READ_TIMEOUT_MS,
      controller.signal
    )
      .then((viewedAt) => {
        if (controller.signal.aborted) return;
        setResponses((current) =>
          current.map((row) =>
            row.responseId === selectedResponseIdForEffects
              ? { ...row, viewedAt: viewedAt ?? row.viewedAt ?? new Date().toISOString() }
              : row
          )
        );
      })
      // Viewed tracking is independent of conversation loading. A failed
      // watermark must not cancel the history read or mark unread data seen.
      .catch(() => undefined);
    return () => controller.abort();
  }, [selectedResponseIdForEffects, selectedViewedAt, messagesReloadNonce]);

  // [MCV-1646] The negotiation editor opens immediately in draft mode; no
  // request is made until Submit Offer.
  const handleCounter = useCallback(() => {
    if (!selected || actions.blocked) return;
    setNegotiationStartDraftIfEmpty(selected.latestNegotiation == null);
    setNegotiationResponseId(selected.responseId);
  }, [selected, actions.blocked]);

  // [MCV-1744] Accepting an application on the listing's terms previews the
  // commitment first, as NegotiationDetails and the B2B workspace do, and
  // only opens the confirmation dialog when this viewer is actually billed.
  // [MCV-1933] A plain accept of an offer does the same, because the fee schedule the paying
  // employer authorizes is sent with that accept. A `notice` (a refused schedule) never falls
  // through to a bare accept.
  const startAccept = useCallback(
    async (
      kind: 'accept' | 'accept_listing',
      notice: string | null = null,
      refusedSchedule: FeeSchedule | null = null
    ) => {
      if (!selected || actions.blocked) return;
      const responseId = selected.responseId;
      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      previewResponseIdRef.current = responseId;
      setPreviewingActivation(true);
      let preview: ActivationPreview;
      try {
        preview = await withRequestDeadline(
          (signal) => getActivationPreview(responseId, signal),
          MARKET_READ_TIMEOUT_MS,
          controller.signal
        );
      } catch (err) {
        if (!mountedRef.current) return;
        if (previewAbortRef.current === controller) setPreviewingActivation(false);
        // Aborted by a selection change or unmount: nothing to report.
        if (err instanceof CanceledError) return;
        console.error('Failed to preview activation billing', err);
        alertView.send({
          type: 'ON_SHOW_ALERT',
          payload: {
            text: 'Could not preview the commitment',
            subText:
              kind === 'accept'
                ? 'Please try accepting the offer again.'
                : 'Please try accepting the application again.',
            type: 'error',
            timeout: 5000,
          },
        });
        return;
      }
      if (!mountedRef.current) return;
      // A newer preview replaced this one. `withRequestDeadline` can still
      // resolve a request that settled before the abort listener ran, so this
      // handler must stand down: `previewingActivation` now belongs to the
      // newer preview, and acting on this result would clear it early and could
      // open the fee dialog or post accept_listing alongside the newer preview.
      if (previewAbortRef.current !== controller) return;
      setPreviewingActivation(false);
      // The selection moved while the preview was in flight: this preview is
      // for a response the user is no longer looking at, so drop it rather
      // than show A's fee and accept B.
      if (selectedResponseIdRef.current !== responseId) return;
      if (preview.isBillable && preview.isCurrentUserPayer) {
        setActivationResponseId(responseId);
        // [MCV-1933] A refusal carries the schedule it was refused over: the only place an
        // application accepted on the listing's terms can show one, since its offer did not exist
        // when the preview was read.
        setActivationPreview(
          refusedSchedule ? { ...preview, feeSchedule: refusedSchedule } : preview
        );
        setActivationKind(kind);
        setActivationNotice(notice);
        setActivationDialogOpen(true);
        return;
      }
      // A refused fee authorization never falls through to a bare accept.
      if (notice != null) return;
      actions.write(kind);
    },
    [selected, actions.blocked, actions.write, alertView]
  );
  const handleAcceptListing = useCallback(() => startAccept('accept_listing'), [startAccept]);
  const handleAccept = useCallback(() => startAccept('accept'), [startAccept]);

  // [MCV-1933] The server refused the accept for its fee schedule (428 not authorized, 409
  // changed since the preview): show the refetched schedule again with the reason.
  const startAcceptRef = useRef(startAccept);
  startAcceptRef.current = startAccept;
  const { feeRefusal, feeRefusalKind, feeRefusalSchedule, consumeFeeRefusal } = actions;
  useEffect(() => {
    if (!feeRefusal) return;
    consumeFeeRefusal();
    void startAcceptRef.current(
      feeRefusalKind === 'accept_listing' ? 'accept_listing' : 'accept',
      feeRefusal === 'FEE_SCHEDULE_CHANGED'
        ? FEE_SCHEDULE_CHANGED_MESSAGE
        : FEE_AUTHORIZATION_REQUIRED_MESSAGE,
      feeRefusalSchedule
    );
  }, [feeRefusal, feeRefusalKind, feeRefusalSchedule, consumeFeeRefusal]);

  const closeActivationDialog = useCallback(() => {
    setActivationDialogOpen(false);
    setActivationPreview(null);
    setActivationResponseId(null);
    setActivationNotice(null);
  }, []);

  // Changing the selection under an open dialog invalidates it: `actions`
  // is rebound to the new response, so Confirm would accept the wrong one.
  useEffect(() => {
    if (activationDialogOpen && activationResponseId !== selectedResponseId)
      closeActivationDialog();
  }, [activationDialogOpen, activationResponseId, selectedResponseId, closeActivationDialog]);

  const handleConfirmActivation = useCallback(
    (feeAuthorization?: FeeAuthorization) => {
      const matches = activationResponseId === selectedResponseIdRef.current;
      closeActivationDialog();
      if (matches) actions.write(activationKind, undefined, feeAuthorization);
    },
    [activationKind, activationResponseId, closeActivationDialog, actions.write]
  );

  const handleNegotiationClose = useCallback(() => {
    setNegotiationResponseId(null);
    void loadResponses();
  }, [loadResponses]);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col p-4 min-[1360px]:p-6"
      data-cy="provider-job-responses-workspace"
    >
      {error && <MarketAlert tone="error">{error}</MarketAlert>}
      {actions.error && !actions.feeRefusal && (
        <MarketAlert tone="error">{actions.error}</MarketAlert>
      )}
      <OpportunityRecoveryNotice
        action={actions.uncertain}
        busy={actions.busy}
        canAcknowledge={actions.canAcknowledge}
        onReview={() => {
          setFilter('all');
          actions.review();
        }}
        onAcknowledge={actions.acknowledge}
        onDiscard={actions.discard}
        discardsDraft
      />
      {actions.readError && !actions.uncertain && (
        <MarketAlert tone="error">
          {actions.readError}
          <p>The write was acknowledged. Dismissing this warning does not resend or undo it.</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <Button
              label="Dismiss refresh warning"
              disabled={actions.busy}
              onClick={actions.discard}
            />
            <Button
              label="Refresh original response"
              disabled={actions.busy}
              onClick={() => {
                setFilter('all');
                actions.review();
              }}
            />
          </div>
        </MarketAlert>
      )}
      {messagesError && (
        <MarketAlert tone="error">
          {messagesError}
          <Button
            type="button"
            size="sm"
            label="Retry messages"
            onClick={() => {
              setMessagesReloadNonce((nonce) => nonce + 1);
              actions.reloadMessages();
            }}
          />
        </MarketAlert>
      )}
      {listingFilterWarning && <MarketAlert tone="warning">{listingFilterWarning}</MarketAlert>}
      {listingId != null && listingFilterWarning == null && (
        <MarketAlert tone="info">Filtered to listing #{listingId}</MarketAlert>
      )}
      <MarketWorkspaceGrid
        dataCy="provider-job-responses-grid"
        detailKey={selected?.responseId}
        sidebar={
          <ProviderResponsesSidebar
            responses={responses}
            selectedResponseId={selected?.responseId ?? null}
            filter={filter}
            includeDeclined={includeDeclined}
            loading={loading}
            onFilter={setFilter}
            onIncludeDeclined={setIncludeDeclined}
            onSelect={setSelectedResponseId}
          />
        }
        detail={
          selected ? (
            <ProviderResponseDetail
              response={selected}
              consentViewerScope={consentViewerScope}
              consentRefreshKey={`${selected.updatedAt}:${messages[messages.length - 1]?.id ?? ''}`}
              consentDisabled={loading || actions.blocked}
              actionsDisabled={loading || actions.blocked || previewingActivation}
              actionLoading={actionLoading}
              onAccept={() => void handleAccept()}
              onAcceptListing={() => void handleAcceptListing()}
              onCounter={handleCounter}
              onWithdraw={() => actions.write('withdraw')}
              onReject={() =>
                confirmationView.send(declineResponseConfirmation(() => actions.write('reject')))
              }
              onConsentChanged={() => {
                void loadResponses();
                actions.reloadMessages();
              }}
            />
          ) : (
            <EmptyPanel label={loading ? 'Loading responses...' : 'No job responses'} />
          )
        }
        conversation={
          selected ? (
            <ProviderResponseConversation
              key={selected.responseId}
              response={selected}
              messages={messages}
              currentUserInfoId={currentUserInfoId}
              loading={messagesLoading}
              sending={actions.blocked}
              error={actions.replyError}
              draft={actions.draft}
              onDraftChange={actions.edit}
              onSend={(body) => actions.write('reply', body)}
            />
          ) : (
            <EmptyPanel label="No response selected" />
          )
        }
      />
      <ResponseNegotiationModal
        open={negotiationResponseId != null}
        responseId={negotiationResponseId}
        responseType="job"
        viewerRole="EMPLOYER"
        startDraftIfEmpty={negotiationStartDraftIfEmpty}
        onDraftSubmitted={() => void loadResponses()}
        onClose={handleNegotiationClose}
      />
      <ActivationFeeDialog
        open={activationDialogOpen}
        preview={activationPreview}
        variant={activationKind === 'accept_listing' ? 'acceptListing' : 'accept'}
        notice={activationNotice}
        onClose={closeActivationDialog}
        onConfirm={handleConfirmActivation}
        onManagePaymentMethod={handleManagePaymentMethod}
      />
    </div>
  );
};

const ProviderResponsesSidebar = ({
  responses,
  selectedResponseId,
  filter,
  includeDeclined,
  loading,
  onFilter,
  onIncludeDeclined,
  onSelect,
}: {
  responses: ProviderJobResponse[];
  selectedResponseId: number | null;
  filter: ProviderJobResponseFilter;
  includeDeclined: boolean;
  loading: boolean;
  onFilter: (filter: ProviderJobResponseFilter) => void;
  onIncludeDeclined: (includeDeclined: boolean) => void;
  onSelect: (responseId: number) => void;
}) => (
  <MarketContentPanel
    className="h-full"
    bodyClassName="flex min-h-0 flex-1 flex-col p-2"
    dataCy="provider-job-responses-sidebar"
    header={
      <>
        <MarketPanelHeader
          title="Job Responses"
          subtitle={`${responses.length} ${responses.length === 1 ? 'response' : 'responses'}`}
        />
        <MarketSegmentedControl
          className="mt-3 w-full"
          fullWidth
          options={(['all', 'active', 'accepted'] as const).map((option) => ({
            label: formatStatusLabel(option),
            value: option,
            cyTag: `provider-job-response-filter-${option}`,
          }))}
          value={filter}
          onChange={onFilter}
        />
        <MarketShowDeclinedToggle
          className="mt-2"
          checked={includeDeclined}
          onChange={onIncludeDeclined}
        />
      </>
    }
    variant="scroll"
  >
    {loading && <MarketLoadingPanel />}
    {!loading &&
      responses.map((response) => {
        const statusLabel = formatResponseStatusLabel(response.status);
        const meta = [
          response.listing.specialtyName,
          formatWorkLocation(response.listing.workLocation),
          response.listing.stateCodes.join(', '),
        ]
          .filter(Boolean)
          .join(' / ');

        return (
          <MarketResponseListItem
            key={response.responseId}
            selected={selectedResponseId === response.responseId}
            title={response.listing.jobTitle}
            subtitle={response.provider.displayName}
            status={getResponseStatusTone(response.status)}
            statusLabel={statusLabel}
            meta={meta}
            aside={
              response.viewedAt == null ? (
                <MarketStatusPill
                  status="warning"
                  label="New"
                  cyTag={`provider-job-response-new-${response.responseId}`}
                />
              ) : null
            }
            onClick={() => onSelect(response.responseId)}
            dataCy={`provider-job-response-card-${response.responseId}`}
          />
        );
      })}
    {!loading && responses.length === 0 && (
      <MarketEmptyPanel
        title="No responses match this filter"
        className="min-[1360px]:-translate-y-12"
      />
    )}
  </MarketContentPanel>
);

const ProviderResponseDetail = ({
  response,
  consentViewerScope,
  consentRefreshKey,
  consentDisabled,
  actionLoading,
  actionsDisabled,
  onAccept,
  onAcceptListing,
  onCounter,
  onWithdraw,
  onReject,
  onConsentChanged,
}: {
  response: ProviderJobResponse;
  consentViewerScope: string;
  consentRefreshKey: string;
  consentDisabled: boolean;
  actionLoading: ActionName | null;
  actionsDisabled: boolean;
  onAccept: () => void;
  onAcceptListing: () => void;
  onCounter: () => void;
  onWithdraw: () => void;
  onReject: () => void;
  onConsentChanged: () => void;
}) => {
  const { listing, provider } = response;
  const canAccept = hasAction(response.availableActions, 'accept');
  const canAcceptListing = hasAction(response.availableActions, 'accept_listing');
  const canCounter = hasAction(response.availableActions, 'counter');
  const canWithdraw = hasAction(response.availableActions, 'withdraw');
  const canReject = hasAction(response.availableActions, 'reject');
  const showActions = canAccept || canAcceptListing || canCounter || canWithdraw || canReject;
  const pendingAcceptanceMessage =
    response.status !== 'PENDING_ACCEPTANCE'
      ? null
      : response.turn === 'YOUR_TURN'
        ? 'The provider accepted the current offer. Review and accept it to complete the agreement.'
        : response.turn === 'THEIR_TURN'
          ? 'You accepted. The provider must confirm before the contract is created.'
          : null;
  const providerViewHref =
    provider.providerId == null
      ? undefined
      : buildProviderViewUrl({
          providerId: provider.providerId,
          responseId: response.responseId,
        });
  const listingFacts = (
    [
      { label: 'Status', value: response.status === 'REJECTED' ? 'Declined' : response.status },
      listing.specialtyName ? { label: 'Specialty', value: listing.specialtyName } : null,
      { label: 'Job Type', value: listing.jobType },
      { label: 'States', value: listing.stateCodes.join(', ') || '-' },
      { label: 'Work Location', value: formatWorkLocation(listing.workLocation) },
      listing.numOfShifts != null ? { label: 'Shifts', value: String(listing.numOfShifts) } : null,
      listing.hoursPerShift != null ? { label: 'Hours/Shift', value: listing.hoursPerShift } : null,
      listing.pricePerHour != null
        ? { label: 'Rate', value: formatHourlyRate(listing.pricePerHour), highlight: true }
        : null,
      listing.startDate || listing.endDate
        ? {
            label: 'Duration',
            value:
              listing.startDate && listing.endDate
                ? `${formatDate(listing.startDate)} - ${formatDate(listing.endDate)}`
                : listing.startDate
                  ? `${formatDate(listing.startDate)}+`
                  : `Until ${formatDate(listing.endDate)}`,
          }
        : null,
    ] as Array<MarketFact | null>
  ).filter((fact): fact is MarketFact => fact !== null);

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      dataCy="provider-job-response-detail"
      variant="scroll"
    >
      <div className="shrink-0 border-b border-grey-900 p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-left">
            <MarketStatusPill status="responded" label="Job Response" />
            <h2 className="sub-heading-sb mt-2 text-content-primary">{listing.jobTitle}</h2>
          </div>
          <MarketTurnPill turn={response.turn} cyTag="provider-job-response-turn" />
        </div>

        <MarketPartySummary
          label="Provider"
          name={provider.displayName}
          description={
            [provider.providerCategory, provider.providerType].filter(Boolean).join(' / ') ||
            'Provider'
          }
          status={
            <MarketStatusPill
              status={provider.masked ? 'masked' : 'unmasked'}
              label={provider.masked ? 'Masked' : 'Unmasked'}
            />
          }
          action={
            providerViewHref ? (
              <a
                href={providerViewHref}
                className="rounded-md border border-primary-50 bg-primary-50 px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-100"
                data-cy="provider-view-link"
              >
                View provider
              </a>
            ) : (
              <button
                type="button"
                className="rounded-md border border-grey-900 bg-grey-950 px-3.5 py-2 text-sm font-semibold text-content-secondary"
                disabled
              >
                View provider
              </button>
            )
          }
        />

        <MarketFactGrid facts={listingFacts} />

        {pendingAcceptanceMessage && (
          <div
            className="mt-5 rounded-lg border border-primary-t-100 bg-primary-t-900/40 p-4 text-left"
            data-cy="job-pending-acceptance-notice"
          >
            <h3 className="text-sm font-semibold text-content-primary">Pending acceptance</h3>
            <p className="mt-1 text-sm text-content-secondary">{pendingAcceptanceMessage}</p>
          </div>
        )}

        {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
        {response.status === 'ACCEPTED' && (
          <div className="mt-5">
            <ContractReadyNotice />
          </div>
        )}

        {showActions && (
          <MarketActionRow
            align="start"
            className="mt-5"
            actions={[
              ...(canAcceptListing
                ? [
                    {
                      label: actionLoading === 'accept_listing' ? 'Accepting...' : 'Accept',
                      cyTag: 'btn-accept-listing-provider-response',
                      disabled: actionsDisabled,
                      onClick: onAcceptListing,
                      colorVariant: 'primary' as const,
                    },
                  ]
                : []),
              ...(canAccept
                ? [
                    {
                      label: actionLoading === 'accept' ? 'Accepting...' : 'Accept',
                      cyTag: 'btn-accept-provider-response',
                      disabled: actionsDisabled,
                      onClick: onAccept,
                      colorVariant: 'primary' as const,
                    },
                  ]
                : []),
              ...(canCounter
                ? [
                    {
                      label:
                        actionLoading === 'counter'
                          ? 'Starting...'
                          : response.latestNegotiation
                            ? 'Counter'
                            : 'Negotiate',
                      cyTag: 'btn-counter-provider-response',
                      disabled: actionsDisabled,
                      onClick: onCounter,
                      colorVariant: 'secondary' as const,
                    },
                  ]
                : []),
              ...(canReject
                ? [
                    {
                      label: actionLoading === 'reject' ? 'Declining...' : 'Decline',
                      cyTag: 'btn-reject-provider-response',
                      disabled: actionsDisabled,
                      onClick: onReject,
                      colorVariant: 'error' as const,
                    },
                  ]
                : []),
              ...(canWithdraw
                ? [
                    {
                      label: actionLoading === 'withdraw' ? 'Withdrawing...' : 'Withdraw',
                      cyTag: 'btn-withdraw-provider-response',
                      disabled: actionsDisabled,
                      onClick: onWithdraw,
                      colorVariant: 'tertiary' as const,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>

      <div className="shrink-0 p-4 sm:p-6">
        <ResponseUnmaskPanel
          responseId={response.responseId}
          viewerScopeKey={consentViewerScope}
          refreshKey={consentRefreshKey}
          disabled={consentDisabled}
          onChanged={onConsentChanged}
        />
        <NegotiationSummary response={response} />
      </div>
    </MarketContentPanel>
  );
};

const NegotiationSummary = ({ response }: { response: ProviderJobResponse }) => {
  const history = useNegotiationChain(response.responseId, API_CALLS.getNegotiationChain);
  const latest = response.latestNegotiation;
  const versions = useMemo(
    () => (history.rows ? jobChainToVersions(history.rows) : null),
    [history.rows]
  );

  return (
    <NegotiationHistory
      rounds={response.negotiationCount}
      latest={latestOf(
        latest && {
          version: latest.version,
          headline: {
            shifts: latest.numOfShifts != null ? String(latest.numOfShifts) : null,
            rate: formatHourlyRate(latest.pricePerHour) || null,
            value: headlineValue(latest.totalContractValue),
          },
        },
        latest?.sender,
        VIEWER_SENDER
      )}
      viewerSender={VIEWER_SENDER}
      versions={versions}
      loading={history.loading}
      error={history.error}
      expanded={history.expanded}
      onToggle={history.toggle}
      termGroups={JOB_TERM_GROUPS}
      termLabel={negotiationHistoryTermLabel}
      formatTerm={formatNegotiationHistoryTerm}
      cy={{
        toggle: 'btn-provider-job-negotiation-history',
        history: 'provider-job-history',
      }}
    />
  );
};

const ProviderResponseConversation = ({
  response,
  messages,
  currentUserInfoId,
  loading,
  sending,
  error,
  draft,
  onDraftChange,
  onSend,
}: {
  response: ProviderJobResponse;
  messages: ProviderJobMessage[];
  currentUserInfoId: number | undefined;
  loading: boolean;
  sending: boolean;
  error: string | null;
  draft: string;
  onDraftChange: (message: string) => void;
  onSend: (message: string) => void;
}) => {
  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
  };

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      variant="scroll"
    >
      <div className="border-b border-grey-900 p-4 text-left sm:p-5">
        <h3 className="body-text-sb">{response.provider.displayName}</h3>
        <div className="mt-2">
          <MarketStatusPill
            status={response.provider.masked ? 'masked' : 'unmasked'}
            label={response.provider.masked ? 'Identity masked' : 'Provider'}
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        <MessagesSection
          messages={messages}
          currentUserInfoId={currentUserInfoId}
          loading={loading}
        />
      </div>
      <MessageComposer
        value={draft}
        onChange={onDraftChange}
        onSend={submit}
        error={error}
        disabled={sending}
        sending={sending}
        masked={response.provider.masked}
        label="Message to provider"
        sendLabel="Send"
        inputTag="provider-job-message-input"
        sendTag="btn-send-provider-job-message"
      />
    </MarketContentPanel>
  );
};

const EmptyPanel = ({ label }: { label: string }) => (
  <MarketContentPanel className="h-full" variant="empty">
    <MarketEmptyPanel title={label} />
  </MarketContentPanel>
);
